import { isAfter, isBefore, parseISO } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { dateFormat, timeFormat, timeZone } from 'src/utils/date-time-format';

export type InputTime = boolean | 'full-hour' | 'gas-day' | 'gas-month';

// dd.mm.yyyy
export const dateRegExp =
  /^\s*([0-9][0-9]).([0-9][0-9]).([0-9][0-9][0-9][0-9])\s*$/;

// dd.mm.yyyy for finding it anywhere in string
const dateRegExpAnywhere =
  /\s*([0-9][0-9]).([0-9][0-9]).([0-9][0-9][0-9][0-9])\s*/;

// dd.mm.yyyy | hh:mm
export const dateTimeRegExp =
  /^\s*([0-9][0-9]).([0-9][0-9]).([0-9][0-9][0-9][0-9])\s+\|\s+([0-9][0-9]):([0-9][0-9])\s*$/;

// all our dates in the FRONTEND are using CET/CEST, but sadly we can't _create_
// a new date for a specific timezone in vanilla JS (we can only render it to a
// certain timezone by using `toLocaleString`) - that's why we need `zonedTimeToUtc`,
// because on the BACKEND we're using UTC
export function toISOString(value: string | null) {
  if (value) {
    const dateTimeMatches = value.match(dateTimeRegExp);
    const dateMatches = value.match(dateRegExp);

    if (dateTimeMatches) {
      const date = parseDateTimeMatches(dateTimeMatches);
      return zonedTimeToUtc(date, timeZone).toISOString();
    } else if (dateMatches) {
      const date = parseDateMatches(dateMatches);
      return zonedTimeToUtc(date, timeZone).toISOString();
    } else {
      return null;
    }
  } else {
    return value;
  }
}

// tries to extract a dd.mm.yyyy date from the string, and if found, returns exactly that or null
export function extractDateFromString(input: string): string | null {
  if (input) {
    const match = input.match(dateRegExpAnywhere);
    if (match) {
      const [fullMatch] = match;
      return fullMatch;
    }
    return null;
  }
  return null;
}

// Transform the given date string to needed input values to match the given input mask.
// See regex above for the format.
export function transformValueToInputMask(
  value: string | null,
  time?: InputTime
) {
  if (!value) {
    return '';
  }

  if (!time) {
    return new Date(value).toLocaleString('de-DE', dateFormat);
  }

  const date = new Date(value);

  const dateString = date.toLocaleString('de-DE', dateFormat);
  const timeString = date.toLocaleString('de-DE', timeFormat);
  return `${dateString} | ${timeString}`;
}

export type Hour =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | '11'
  | '12'
  | '13'
  | '14'
  | '15'
  | '16'
  | '17'
  | '18'
  | '19'
  | '20'
  | '21'
  | '22'
  | '23';

type AttachTimeParams = {
  originalDate: Date;
  hours: string;
  minutes: string;
  time: InputTime;
  defaultHour?: Hour;
};

// Attatch time to given date or fallback to current time if no custom time is set
export const attachTime = ({
  originalDate,
  hours,
  minutes,
  time,
  defaultHour,
}: AttachTimeParams) => {
  // Clone date to be immutable and trigger effects correctly.
  const date = utcToZonedTime(originalDate.toISOString(), timeZone);
  if (time === 'gas-day' || time === 'gas-month') {
    date.setHours(6);
    date.setMinutes(0);
  } else if (time === 'full-hour') {
    date.setHours(
      hours === ''
        ? defaultHour == null
          ? getCurrentHourInPrismaTimeZone()
          : Number(defaultHour)
        : Number(hours)
    );
    date.setMinutes(0);
  } else {
    date.setHours(
      hours === ''
        ? defaultHour == null
          ? getCurrentHourInPrismaTimeZone()
          : Number(defaultHour)
        : Number(hours)
    );
    date.setMinutes(
      minutes === '' ? getCurrentMinutesInPrismaTimeZone() : Number(minutes)
    );
  }
  // this is now the correct time in Europe/Berlin, but needs to be converted to local tz so that UTC values of saved date match the real utc values
  const dateInLocalTz = utcToZonedTime(
    zonedTimeToUtc(date, timeZone),
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  return dateInLocalTz;
};

function getCurrentHourInPrismaTimeZone() {
  return utcToZonedTime(new Date().toISOString(), timeZone).getHours();
}

function getCurrentMinutesInPrismaTimeZone() {
  return utcToZonedTime(new Date().toISOString(), timeZone).getMinutes();
}

export function parseDateMatches(matches: string[]) {
  const [_, day, month, year] = matches;
  return parseISO(`${year}-${month}-${day}`);
}

function parseDateTimeMatches(values: string[]) {
  const [_, day, month, year, hour, minute] = values;
  return parseISO(`${year}-${month}-${day} ${hour}:${minute}`);
}

export function getPlaceholder(time: InputTime) {
  if (time === true) return dateTimeMaskPlaceholder;
  if (time === 'full-hour') return dateTimeFullHourMaskPlaceholder;
  if (time === 'gas-day') return dateTimeGasDayMaskPlaceholder;
  if (time === 'gas-month') return dateTimeGasMonthMaskPlaceholder;
  return dateMaskPlaceholder;
}

export function getMask(time: InputTime) {
  if (time === true) return dateTimeInputMask;
  if (time === 'full-hour') return dateTimeFullHourInputMask;
  if (time === 'gas-day') return dateTimeGasDayInputMask;
  if (time === 'gas-month') return dateTimeGasMonthInputMask;
  return dateInputMask;
}

export function validateDateInput(time: InputTime, value: string | null) {
  if (value === null) return;
  if (value === '') return;
  if (value === getPlaceholder(time)) return;

  const matches = value.match(time ? dateTimeRegExp : dateRegExp);
  if (!matches) return 'Date';

  const date = time ? parseDateTimeMatches(matches) : parseDateMatches(matches);

  // avoid "date-fns-tz" bug
  // see https://github.com/marnusw/date-fns-tz/issues/87
  if (date.getFullYear() < 100) return 'Date';
  if (isNaN(date.valueOf())) return 'Date';
}

export function isFullHourDate(value: string | null): boolean {
  if (!value) return false;

  const date = parseISO(value);
  return (
    !isNaN(date.valueOf()) &&
    date.getMinutes() === 0 &&
    date.getSeconds() === 0 &&
    date.getMilliseconds() === 0
  );
}

export function isStartOfGasMonthDate(value: string | null): boolean {
  if (!value) return false;

  const date = parseISO(value);
  const valueInBerlin = utcToZonedTime(
    zonedTimeToUtc(date, Intl.DateTimeFormat().resolvedOptions().timeZone),
    timeZone
  );

  return (
    !isNaN(date.valueOf()) &&
    date.getMinutes() === 0 &&
    date.getSeconds() === 0 &&
    date.getMilliseconds() === 0 &&
    valueInBerlin.getHours() === 6 &&
    valueInBerlin.getDate() === 1
  );
}

export function checkBlockedDate({
  midnightInBerlin,
  time,
  minBookingDate,
  maxBookingDate,
}: {
  midnightInBerlin: Date;
  time: InputTime;
  minBookingDate?: Date;
  maxBookingDate?: Date;
}) {
  // date was already normalized before its passed to `useDay`,
  // so we know it's midnightInBerlin (but as UTC, so either 22:00 or 23:00).
  // however we might not need midnight but the start of the gas day.
  // so we need to convert it back to CET/CEST, set the hour and convert back to UTC.
  const berlinDate = utcToZonedTime(midnightInBerlin, timeZone);
  if (time === 'gas-month' || time === 'gas-day') berlinDate.setHours(6);
  const currentDate = zonedTimeToUtc(berlinDate, timeZone);

  // start checking, if date should be blocked
  const blockedByGasMonth = time === 'gas-month' && berlinDate.getDate() !== 1;

  const blockedByMinDate =
    !!minBookingDate && isBefore(currentDate, minBookingDate);

  const blockedByMaxDate =
    !!maxBookingDate && isAfter(currentDate, maxBookingDate);

  return blockedByGasMonth || blockedByMinDate || blockedByMaxDate;
}

const dateInputMask = '99.99.9999';
const dateMaskPlaceholder = 'DD.MM.YYYY';

const dateTimeInputMask = '99.99.9999 | 99:99';
const dateTimeMaskPlaceholder = 'DD.MM.YYYY | HH:mm';

const dateTimeFullHourInputMask = '99.99.9999 | 99:00';
const dateTimeFullHourMaskPlaceholder = 'DD.MM.YYYY | HH:00';

const dateTimeGasDayInputMask = '99.99.9999 | 06:00';
const dateTimeGasDayMaskPlaceholder = 'DD.MM.YYYY | 06:00';

const dateTimeGasMonthInputMask = '01.99.9999 | 06:00';
const dateTimeGasMonthMaskPlaceholder = '01.MM.YYYY | 06:00';
